<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :module-enabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :inputs="inputs"
    @save="save"
  >
    <template #measurement_datasource.input="data">
      <v-col cols="3">
        <data-input v-bind="data" v-model="selectedDataSourceProps.datasource" @change="clearColumns" />
      </v-col>
    </template>
    <template #coordinate_x_column.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSourceProps.settings['coordinate_x_column']"
          @change="selectedDataSourcePropsChanged = true"
        />
      </v-col>
    </template>
    <template #coordinate_y_column.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSourceProps.settings['coordinate_y_column']"
          @change="selectedDataSourcePropsChanged = true"
        />
      </v-col>
    </template>
    <template #coordinate_z_column.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSourceProps.settings['coordinate_z_column']"
          @change="selectedDataSourcePropsChanged = true"
        />
      </v-col>
    </template>
    <template #description_column.input="data">
      <v-col cols="3">
        <data-input
          v-bind="data"
          v-model="selectedDataSourceProps.settings['description_column']"
          @change="selectedDataSourcePropsChanged = true"
        />
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'MeasurementsSettings',
  components: {
    AdminWrapper,
    DataInput,
  },
  data: () => ({
    selectedDataSourceProps: {
      settings: {
        coordinate_x_column: '',
        coordinate_y_column: '',
        coordinate_z_column: '',
        description_column: '',
      },
      datasource: '',
    },
    selectedDataSourcePropsChanged: false,
    isFormFetched: false,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.MEASUREMENTS_MODULE.verbose_name'),
    dataSources: get('layers/metadata'),
    isModuleEnabled: get('admin/settingsValues@measurements_module_enabled'),
    dataSourceSettingsName() {
      return 'measurements_datasource';
    },
    pointDataSources() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => dataSource.geometry_type === 'point' && dataSource.scope === 'core' && !dataSource.module
      );
    },
    dataSource() {
      return this.dataSources[this.selectedDataSourceProps.datasource];
    },
    dataSourceTextAttributes() {
      return (
        this.dataSource?.attributes_schema.attributes.filter(
          attr => attr.data_type.name === 'text' && !attr.read_only
        ) || []
      );
    },
    dataSourceFloatAttributes() {
      return (
        this.dataSource?.attributes_schema.attributes.filter(
          attr => attr.data_type.name === 'float' && !attr.read_only
        ) || []
      );
    },
    dataSourceNumberAttributes() {
      return (
        this.dataSource?.attributes_schema.attributes.filter(
          attr => ['float', 'decimal', 'integer'].includes(attr.data_type.name) && !attr.read_only
        ) || []
      );
    },
    inputs() {
      return [
        {
          name: 'measurement_datasource',
          translationPath: 'admin.datasourcesTypeMeasurement',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.pointDataSources,
          itemText: 'verbose_name',
          itemValue: 'name',
        },
        {
          name: 'coordinate_x_column',
          translationPath: 'admin.attributeCoordinateColumnX',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.dataSourceFloatAttributes,
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.dataSource,
        },
        {
          name: 'coordinate_y_column',
          translationPath: 'admin.attributeCoordinateColumnY',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.dataSourceFloatAttributes,
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.dataSource,
        },
        {
          name: 'coordinate_z_column',
          translationPath: 'admin.attributeCoordinateColumnZ',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.dataSourceNumberAttributes,
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.dataSource,
        },
        {
          name: 'description_column',
          translationPath: 'admin.attributeCoordinateColumnDescription',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          align: 'start',
          items: this.dataSourceTextAttributes,
          itemText: 'verbose_name',
          itemValue: 'name',
          disabled: !this.dataSource,
        },
      ];
    },
    moduleEnabler() {
      return {
        name: 'measurements_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
  },
  methods: {
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getModulesMapping: call('admin/getModulesMapping'),
    clearColumns() {
      this.selectedDataSourceProps.settings.description_column = '';
      this.selectedDataSourceProps.settings.coordinate_x_column = '';
      this.selectedDataSourceProps.settings.coordinate_y_column = '';
      this.selectedDataSourceProps.settings.coordinate_z_column = '';
      this.selectedDataSourcePropsChanged = true;
    },
    async init() {
      await Promise.all([this.getModulesMapping(), this.getDataSources({ with_attributes_schema: true })]);
      if (this.isModuleEnabled) {
        await this.updateSelectedDataSource();
      }
      this.isFormFetched = true;
    },
    async save() {
      if (!this.selectedDataSourcePropsChanged) {
        return;
      }
      const payload = { name: this.dataSourceSettingsName, settings: this.selectedDataSourceProps };
      try {
        await this.editDatasourceSettings(payload);
        this.selectedDataSourcePropsChanged = false;
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch (error) {
        console.log('  error', error);
      }
    },
    async updateSelectedDataSource() {
      try {
        const r = await this.getDatasourceSettings(this.dataSourceSettingsName);
        const { datasource, settings } = r;
        this.selectedDataSourceProps = {
          datasource,
          settings,
        };
        this.selectedDataSourcePropsChanged = false;
      } catch {
        this.selectedDataSourceProps = {
          settings: {
            coordinate_x_column: '',
            coordinate_y_column: '',
            coordinate_z_column: '',
            description_column: '',
          },
          datasource: '',
        };
        this.selectedDataSourcePropsChanged = false;
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
